// src/theme.js
import { createTheme, responsiveFontSizes } from "@mui/material/styles";

/* ------------------------------------------------------
   CHATGPT FONT STACK — VARIABLE INTER
------------------------------------------------------- */
const chatgptFontFamily =
  "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";

/* ------------------------------------------------------
   BASE TYPOGRAPHY — EXACT CHATGPT STYLE
------------------------------------------------------- */
const chatgptTypography = {
  fontFamily: chatgptFontFamily,

  h1: {
    fontWeight: 600,
    fontSize: "2.2rem",
    letterSpacing: "-0.02em",
  },
  h2: {
    fontWeight: 500,
    fontSize: "1.8rem",
    letterSpacing: "-0.01em",
  },
  h3: {
    fontWeight: 500,
    fontSize: "1.5rem",
  },
  h6: {
    fontWeight: 500,
    fontSize: "1.1rem",
  },

  body1: {
    fontWeight: 400,
    fontSize: "0.95rem",
    lineHeight: 1.55,
  },

  button: {
    textTransform: "none",
    fontWeight: 500,
    letterSpacing: "0em",
  },
};

/* ------------------------------------------------------
   SHARED SPACING OVERRIDES (ChatGPT 4–8–12–16–20 grid)
------------------------------------------------------- */
const chatgptSpacingOverrides = {
  MuiPaper: {
    styleOverrides: {
      root: {
        padding: "16px",
        borderRadius: "12px",
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        padding: "16px",
        borderRadius: "12px",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        padding: "8px 16px",
        borderRadius: "10px",
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        marginBottom: "12px",
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        padding: "20px",
        borderRadius: "12px",
      },
    },
  },
};

/* ------------------------------------------------------
   LIGHT THEME
------------------------------------------------------- */
export const lightTheme = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "light",
      primary: { main: "#0057A4" },
      secondary: { main: "#FB8C00" },
      background: {
        default: "#f4f8fc",
        paper: "#ffffff",
      },
      text: {
        primary: "#1e293b",
        secondary: "#475569",
      },
    },

    shape: { borderRadius: 10 },
    spacing: 4, // ChatGPT spacing system
    typography: chatgptTypography,

    components: {
      ...chatgptSpacingOverrides,

      MuiButton: {
        defaultProps: {
          disableElevation: true,
          disableRipple: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 10,
            fontFamily: theme.typography.fontFamily,
            fontWeight: 600,
            fontSize: "0.875rem",
            textTransform: "none",
            letterSpacing: "0.03em",
            lineHeight: 1.4,
            padding: "8px 16px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: theme.shadows[2],
            },
          }),
        },
      },

      MuiLoadingButton: {
        defaultProps: {
          disableElevation: true,
          disableRipple: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 10,
            fontFamily: theme.typography.fontFamily,
            fontWeight: 600,
            fontSize: "0.875rem",
            textTransform: "none",
            letterSpacing: "0.03em",
            padding: "8px 16px",
          }),
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            color: "inherit",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: "16px",
            padding: "20px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.20)",
            backdropFilter: "blur(12px)",
            backgroundColor: "rgba(255,255,255,0.92)",
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            paddingBottom: "4px",
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          head: { textTransform: "capitalize", fontWeight: 700 },
          root: { textTransform: "capitalize" },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            "& .MuiDataGrid-columnHeaderTitle": {
              textTransform: "capitalize",
            },
            "& .MuiDataGrid-cellContent": {
              textTransform: "capitalize",
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { textTransform: "capitalize" },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: { textTransform: "capitalize" },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: { textTransform: "capitalize" },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: { textTransform: "capitalize" },
        },
      },
    },
  })
);

/* ------------------------------------------------------
   DARK THEME
------------------------------------------------------- */
export const darkTheme = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "dark",
      primary: { main: "#347ff7" },
      secondary: { main: "#FB8C00" },
      background: {
        default: "#0b192f",
        paper: "#13233f",
      },
      text: {
        primary: "#e2e8f0",
        secondary: "#94a3b8",
      },
    },

    shape: { borderRadius: 10 },
    spacing: 4, // ChatGPT spacing system
    typography: chatgptTypography,

    components: {
      ...chatgptSpacingOverrides,

      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: "rgba(20,20,30,0.85)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            padding: "20px",
          },
        },
      },
    },
  })
);
